import {
  EXAMPLE_SESSION_LEVELS,
  MAX_SESSION_LEVELS,
  sessionConfigSchema,
  type SessionKey,
  type SessionLevel,
} from '@agent-platform/shared';
import { ArrowDown, ArrowUp, MessagesSquare, NotebookPen, Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const KEY_LABELS: Record<SessionKey['source'], string> = {
  none: 'Shared by everyone',
  variable: 'A request variable',
  thread: 'The thread id',
  caller: 'The caller (API key / console user)',
};

/** "global → store (store_id) → user (user_id)" */
export function levelsSummary(levels: SessionLevel[]): string {
  return levels
    .map((l) =>
      l.key.source === 'variable'
        ? `${l.name} (${l.key.variable})`
        : l.key.source === 'none'
          ? l.name
          : `${l.name} (${l.key.source})`,
    )
    .join(' → ');
}

function numberValue(value: string, min: number, max: number, fallback: number) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n))) : fallback;
}

/**
 * Edits an agent's session levels (broad → specific). Each level keeps shared notes and/or the
 * conversation; the conversation lives at the most specific level a request identifies.
 */
export function SessionLevelsEditor({
  levels,
  disabled,
  onChange,
}: {
  levels: SessionLevel[];
  disabled: boolean;
  onChange: (levels: SessionLevel[]) => void;
}) {
  const issues = useMemo(() => {
    const parsed = sessionConfigSchema.safeParse({ levels });
    return parsed.success
      ? []
      : parsed.error.issues.map((i) => {
          const at = i.path[1];
          return `${typeof at === 'number' && levels[at] ? `${levels[at]!.name || `Level ${at + 1}`}: ` : ''}${i.message}`;
        });
  }, [levels]);

  const update = (i: number, patch: Partial<SessionLevel>) =>
    onChange(levels.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const move = (i: number, delta: -1 | 1) => {
    const next = [...levels];
    const [moved] = next.splice(i, 1);
    next.splice(i + delta, 0, moved!);
    onChange(next);
  };
  const add = () => {
    let n = levels.length + 1;
    while (levels.some((l) => l.name === `level_${n}`)) n++;
    onChange([
      ...levels,
      {
        name: `level_${n}`,
        description: '',
        key: { source: 'variable', variable: `level_${n}_id` },
        history: { enabled: false, maxMessages: 20, maxTokens: 8_000 },
        notes: { enabled: true, maxChars: 4_000, writable: true },
      },
    ]);
  };
  const conversation = [...levels].reverse().find((l) => l.history.enabled);

  return (
    <div className="grid gap-4">
      <div className="text-muted-foreground grid gap-1 text-sm">
        <p>
          Levels go from broad to specific. Each request uses the levels it can identify: a level
          keyed by a variable applies only when the request has that value (an endpoint mapping
          prompt variable, or a playground request context value with the same name). Map it from a
          value your backend sets: whoever controls the value chooses the session.
        </p>
        <p>
          <strong className="text-foreground">Notes</strong> are shared by everyone at that level;
          the agent reads them and updates them with <code>update_session_memory</code>. The{' '}
          <strong className="text-foreground">conversation</strong> lives at the most specific level
          that keeps history, so one user’s messages never reach another user.
        </p>
      </div>

      {levels.length === 0 && (
        <div className="grid justify-items-start gap-2 rounded-xl border border-dashed p-4 text-sm">
          <span>No levels yet.</span>
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={() => onChange(EXAMPLE_SESSION_LEVELS)}
          >
            Start with global → store → user
          </Button>
        </div>
      )}

      <ol className="grid gap-3">
        {levels.map((level, i) => (
          <li
            key={i}
            className="bg-card grid gap-3 rounded-xl border p-3"
            data-testid="session-level"
          >
            <div className="flex items-center gap-2">
              <span className="bg-muted text-muted-foreground flex size-6 items-center justify-center rounded-full text-xs font-medium">
                {i + 1}
              </span>
              <span className="text-muted-foreground text-xs">
                {i === 0 ? 'Broadest' : i === levels.length - 1 ? 'Most specific' : ''}
              </span>
              <div className="ml-auto flex gap-1">
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label={`Move ${level.name} up`}
                  disabled={disabled || i === 0}
                  onClick={() => move(i, -1)}
                >
                  <ArrowUp />
                </Button>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label={`Move ${level.name} down`}
                  disabled={disabled || i === levels.length - 1}
                  onClick={() => move(i, 1)}
                >
                  <ArrowDown />
                </Button>
                <Button
                  size="icon-sm"
                  variant="ghost-destructive"
                  aria-label={`Remove ${level.name}`}
                  disabled={disabled}
                  onClick={() => onChange(levels.filter((_, j) => j !== i))}
                >
                  <Trash2 />
                </Button>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-[10rem_1fr]">
              <div className="grid gap-1.5">
                <Label htmlFor={`level-${i}-name`}>Name</Label>
                <Input
                  id={`level-${i}-name`}
                  className="font-mono"
                  value={level.name}
                  disabled={disabled}
                  onChange={(e) => update(i, { name: e.target.value.toLowerCase() })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor={`level-${i}-description`}>Description</Label>
                <Input
                  id={`level-${i}-description`}
                  value={level.description}
                  placeholder="Shown to the agent, e.g. “One per store”"
                  disabled={disabled}
                  onChange={(e) => update(i, { description: e.target.value })}
                />
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-[1fr_12rem]">
              <div className="grid gap-1.5">
                <Label htmlFor={`level-${i}-key`}>Identified by</Label>
                <Select
                  value={level.key.source}
                  disabled={disabled}
                  onValueChange={(source) =>
                    update(i, {
                      key:
                        source === 'variable'
                          ? { source, variable: `${level.name}_id` }
                          : ({ source } as SessionKey),
                    })
                  }
                >
                  <SelectTrigger id={`level-${i}-key`} aria-label="Identified by">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(KEY_LABELS) as SessionKey['source'][]).map((s) => (
                      <SelectItem key={s} value={s}>
                        {KEY_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {level.key.source === 'variable' && (
                <div className="grid gap-1.5">
                  <Label htmlFor={`level-${i}-variable`}>Variable</Label>
                  <Input
                    id={`level-${i}-variable`}
                    className="font-mono"
                    value={level.key.variable}
                    disabled={disabled}
                    onChange={(e) =>
                      update(i, { key: { source: 'variable', variable: e.target.value } })
                    }
                  />
                </div>
              )}
            </div>

            <div className="grid gap-2 rounded-lg border p-2.5">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={level.notes.enabled}
                  disabled={disabled}
                  onChange={(e) =>
                    update(i, { notes: { ...level.notes, enabled: e.target.checked } })
                  }
                />
                <NotebookPen className="size-3.5" aria-hidden /> Shared notes
              </label>
              {level.notes.enabled && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pl-6 text-xs">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={level.notes.writable}
                      disabled={disabled}
                      onChange={(e) =>
                        update(i, { notes: { ...level.notes, writable: e.target.checked } })
                      }
                    />
                    The agent may update them
                  </label>
                  <label className="flex items-center gap-2">
                    Max characters
                    <Input
                      type="number"
                      className="h-7 w-24"
                      value={level.notes.maxChars}
                      disabled={disabled}
                      onChange={(e) =>
                        update(i, {
                          notes: {
                            ...level.notes,
                            maxChars: numberValue(e.target.value, 100, 20_000, 4_000),
                          },
                        })
                      }
                    />
                  </label>
                </div>
              )}
            </div>

            <div className="grid gap-2 rounded-lg border p-2.5">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={level.history.enabled}
                  disabled={disabled}
                  onChange={(e) =>
                    update(i, { history: { ...level.history, enabled: e.target.checked } })
                  }
                />
                <MessagesSquare className="size-3.5" aria-hidden /> Keeps the conversation
                {conversation === level && (
                  <span className="text-muted-foreground font-normal">
                    (the conversation level when identified)
                  </span>
                )}
              </label>
              {level.history.enabled && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pl-6 text-xs">
                  <label className="flex items-center gap-2">
                    Context budget: last
                    <Input
                      type="number"
                      aria-label="Max messages"
                      className="h-7 w-20"
                      value={level.history.maxMessages}
                      disabled={disabled}
                      onChange={(e) =>
                        update(i, {
                          history: {
                            ...level.history,
                            maxMessages: numberValue(e.target.value, 1, 200, 20),
                          },
                        })
                      }
                    />
                    messages, up to
                    <Input
                      type="number"
                      aria-label="Max tokens"
                      className="h-7 w-24"
                      value={level.history.maxTokens}
                      disabled={disabled}
                      onChange={(e) =>
                        update(i, {
                          history: {
                            ...level.history,
                            maxTokens: numberValue(e.target.value, 100, 200_000, 8_000),
                          },
                        })
                      }
                    />
                    tokens
                  </label>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>

      {issues.length > 0 && (
        <ul className="text-destructive list-disc pl-5 text-sm" aria-label="Problems">
          {issues.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
      <Button
        size="sm"
        variant="outline"
        className="justify-self-start"
        disabled={disabled || levels.length >= MAX_SESSION_LEVELS}
        onClick={add}
      >
        <Plus aria-hidden /> Add level
      </Button>
    </div>
  );
}
